/**
 * GRN (Goods Receipt Note) Constants
 * Centralized constants for GRN module status codes, configuration values, and numeric constraints
 * Now with TypeScript type safety using 'as const' assertions
 */

// ============================================
// GRN STATUS CODES
// ============================================
export const GRN_STATUS = {
  SAVED: "2",
  HOLD: "3",
  SUBMIT: "1",
  APPROVE: "4",
  REVOKE: "5",
  REVERSE: "6",
  RETURN: "6",
  APPROVE_RETURN: "8",
} as const;

export const RETURN_STATUS_CODE= [9, 10, 11, 12, 13] as const;

export type GrnStatusValue = typeof GRN_STATUS[keyof typeof GRN_STATUS];

// ============================================
// TRANSACTION CATEGORY CODES
// ============================================
export const GRN_TRCATCODE = {
  RETURN: "2",
} as const;

// ============================================
// RETURN HISTORY FILTERING
// ============================================
export const GRN_RETURN_HISTORY = {
  VISIBLE_STATUSES: ["10", "11"],
  DEFAULT_STATUS: "10",
  GRID_ID: "field",
  STATUS_FALLBACK_COLOR: "#0000",
} as const;

// ============================================
// APPROVER VIEW FILTERING
// ============================================
export const GRN_APPROVER_FILTER = {
  TRCATCODE: "1",
  EXCLUDED_STATUS: "2",
  INITIAL_VISIBLE_STATUSES: ["1", "3"],
  GRID_ID: "field",
  STATUS_COLOR_FALLBACK: "#000000",
  SWITCH_ARIA_LABEL: "controlled",
  ORIGIN: "ViewApprover",
} as const;

export const GRN_APPROVER_FILTER_MYACTION = {
  TRCATCODE: "1",
  EXCLUDED_STATUS:["1", "3","9"],
  EXCLUDED_STATUS_API:["1", "3",],
  INITIAL_VISIBLE_STATUSES: ["1", "3","9"],
  GRID_ID: "field",
  STATUS_COLOR_FALLBACK: "#000000",
  SWITCH_ARIA_LABEL: "controlled",
  ORIGIN: "ViewApprover",
} as const;
// ============================================
// APPROVER STATUS GATES
// ============================================
export const GRN_APPROVER_STATUS = {
  RETURN_ALLOWED: "1",
  APPROVE_REJECT_ALLOWED: "3",
} as const;

// ============================================
// GRN RETURN STATUS CODES
// ============================================
export const GRN_RETURN_STATUS = {
  RETURNED: "7",
  DRAFT: "12",
} as const;

// ============================================
// RETURN PROCESS STATUS CODES
// ============================================
export const GRN_RETURN_PROCESS_STATUS = {
  APPROVAL_REQUIRED: "9",
  DIRECT_RETURN: "10",
    DRAFT: "12",
} as const;

// ============================================
// VIEW HISTORY FILTERING
// ============================================
export const GRN_HISTORY_FILTER = {
  TRCATCODE: "1",
  VISIBLE_STATUSES: ["4", "5","9","10","11","12"],
  VISIBLE_STATUSES_FLOW2: ["4","9","10","11","12"],
  DEFAULT_STATUS: "4",
  GRID_ID: "field",
  STATUS_FALLBACK_COLOR: "#0000",
  ORIGIN: "ViewInitiator",
} as const;

export const GRN_HISTORY = {
  TRCATCODE: "1",
  VISIBLE_STATUSES: ["4", "5"],
  DEFAULT_STATUS: "4",
  GRID_ID: "field",
  STATUS_FALLBACK_COLOR: "#0000",
  ORIGIN: "Viewhistory",
} as const;

// ============================================
// RETURN APPROVAL FILTERING
// ============================================
export const GRN_RETURN_APPROVAL_FILTER = {
  TRCATCODE: "2",
  EXCLUDED_STATUSES: ["12", "13"],
  INITIAL_STATUS: "9",
  GRID_ID: "field",
  STATUS_FALLBACK_COLOR: "#0000",
  ORIGIN: "ViewApprover",
  ACTION_APPROVER_RETURN:"ActionApproverreturb"
} as const;

// ============================================
// VIEW RETURN GRN FILTERING
// ============================================
export const GRN_VIEW_RETURN_FILTER = {
  VIEW_TYPE_RETURN: "return",
  VIEW_TYPE_APPROVED: "approved",
    VISIBLE_STATUSES: ["4","9","10","11","12"],
  TRCATCODE_APPROVED: "1",
  TRCATCODE_RETURN: "2",
  APPROVED_STATUS: "4",

  RETURN_EXCLUDED_STATUSES_ALL: ["13"],
  RETURN_EXCLUDED_STATUSES_DEFAULT: ["13", "9", "11"],
  RETURN_DEFAULT_STATUS: "12",
  RETURN_EDIT_DISABLED: ["9", "11", "13"],
  GRID_ID: "field",
  STATUS_FALLBACK_COLOR: "#000000",
  STATUS_FALLBACK_COLOR_RETURN: "#0000",
  SIDEBAR_TITLE: "Approved",
  ACTION_VIEW_APPROVED: "Viewapp",
  ACTION_VIEW_RETURN: "Viewret",
  ORIGIN_RETURN: "ReturnGrn",
   ORIGIN_EDIT_RETURN: "editReturnGrn",
} as const;
// ============================================
// INITIATOR GRN FILTERING
// ============================================
export const GRN_INITIATOR_FILTER = {
  TRCATCODE: "1",
  EXCLUDED_STATUSES: ["1", "3", "8", "5", "6"],
  MAIN_STATUSES_WITH_APPROVAL: ["2", "6", "7"],
  MAIN_STATUSES_DEFAULT: ["2", "7"],
  FULL_ALLOWED_EXCLUSIONS: ["1", "3", "8", "5", "6"],
  GRID_ID: "field",
  STATUS_FALLBACK_COLOR: "#000000",
  ORIGIN: "ViewInitiator",
    RT_TRCATCODE: "1",
} as const;

export const MYACTION_INITIATOR_FILTER = {
  TRCATCODE: "1",
  EXCLUDED_STATUSES: ["1", "3", "8", "5", "6"],
  MAIN_STATUSES_WITH_APPROVAL: ["2", "6", "7","12"],
  MAIN_STATUSES_DEFAULT: ["2",  "7","12"],
  FULL_ALLOWED_EXCLUSIONS: ["1", "3", "8", "5", "6"],
  GRID_ID: "field",
  STATUS_FALLBACK_COLOR: "#000000",
  ORIGIN: "ViewInitiator",
    RT_TRCATCODE: "1",
} as const;

// Status display mapping
export const GRN_STATUS_MAP = {
  1: { label: "SUBMITTED", color: "primary" },
  2: { label: "SAVED", color: "info" },
  3: { label: "HOLD", color: "warning" },
  4: { label: "APPROVED", color: "success" },
  5: { label: "REVOKED", color: "error" },
  6: { label: "RETURNED", color: "warning" },
  8: { label: "APPROVE RETURN", color: "success" },
} as const;

// ============================================
// NOTE TYPE CODES
// ============================================
export const NOTE_TYPE = {
  GRN: "GRN",
  RETURN: "RN",
  REJECT: "REJ",
  DEBIT: "DN",
  DELIVERY: "DL",
} as const;

export type NoteType = typeof NOTE_TYPE[keyof typeof NOTE_TYPE];

// ============================================
// DOCUMENT TYPE CODES
// ============================================
export const DOC_TYPE = {
  DEFAULT: "0",
  REJECT: "1",
  CUSTOM: "custom",
} as const;

export type DocType = typeof DOC_TYPE[keyof typeof DOC_TYPE];

// ============================================
// TAX CODES
// ============================================
export const TAX_CODE = {
  BILL_LEVEL: "1",
  PRODUCT_LEVEL: "0",
} as const;

export type TaxCode = typeof TAX_CODE[keyof typeof TAX_CODE];

// ============================================
// BOOLEAN FLAGS (Y/N)
// ============================================
export const FLAG = {
  YES: "Y",
  NO: "N",
} as const;

export type YesNoFlag = typeof FLAG[keyof typeof FLAG];

// ============================================
// REASON CATEGORY CODES
// ============================================
export const REASON_CATEGORY = {
  REJECT_ITEMS: "7",
} as const;

// ============================================
// APPROVAL ACTION REASON CATEGORY CODES
// ============================================
export const GRN_APPROVAL_REASON_CATEGORY = {
  APPROVE: "1",
  REJECT: "2",
  RETURN: "3",
} as const;

// ============================================
// PARAMETER IDS
// ============================================
export const PARAM_ID = {
  APPROVAL_WORKFLOW: 24,
} as const;

// ============================================
// NUMERIC CONSTRAINTS
// ============================================
export const GRN_CONSTRAINTS = {
  // File upload limits
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  MAX_FILE_SIZE_MB: 5,

  // Remark constraints
  REMARK_MAX_LENGTH: 300,

  // Percentage constraints
  MIN_PERCENTAGE: 0,
  MAX_PERCENTAGE: 100,
  PERCENTAGE_DECIMAL_PLACES: 2,

  // Amount constraints
  MIN_AMOUNT: 0,
  MAX_DIGITS_AMOUNT: 9,
  AMOUNT_DECIMAL_PLACES: 2,

  // Quantity constraints
  MIN_QUANTITY: 0,

  // UI constraints
  DROPDOWN_MAX_HEIGHT: 150,
  ICON_SIZE: 24,
  ICON_SIZE_SMALL: 10,
  ICON_SIZE_LARGE: 20,
  HELPER_TEXT_FONT_SIZE: 12,

  // Timeout values
  NAVIGATION_TIMEOUT: 1000, // milliseconds
  DEFAULT_WAIT_TIME: 2000, // milliseconds
} as const;

// ============================================
// DIALOG UI SETTINGS
// ============================================
export const GRN_DIALOG = {
  MAX_WIDTH: "sm",
  PREVIEW_MAX_WIDTH: "lg",
} as const;

// ============================================
// RETURN GRN UI SETTINGS
// ============================================
export const GRN_RETURN_UI = {
  SIDEBAR_TOP: 64,
  SIDEBAR_WIDTH: 320,
  SIDEBAR_WIDTH_MOBILE: "80vw",
  SIDEBAR_HIDDEN_OFFSET: "-320px",
  SIDEBAR_TRANSITION: "right 0.3s ease-in-out",
} as const;

// ============================================
// LOCAL STORAGE KEYS
// ============================================
export const GRN_STORAGE_KEYS = {
  RETURNED_PRODUCTS: "returnedProducts",
  BILLING_SUMMARY: "billingSummary",
  GRN_VIEW_DATA: "grn-view-data",
  GRN_PAGE_DATA: "grn-page-data",
  NEXT_PROCESS_RESPONSE: "nextProcessResponse",
} as const;

// ============================================
// VIEW DETAIL GRN SETTINGS
// ============================================
export const GRN_VIEW_DETAIL = {
  PERSIST_WINDOW_MS: 2 * 60 * 60 * 1000, // 2 hours
  HOLD_LIKE_STATUSES: ["3", "7"],
  APPROVER_HIDE_STATUSES: ["6", "7", "5", "8", "4", "13", "11", "10"],
  RETURN_APPROVAL_STATUSES: ["9", "10", "11", "12", "13"],
  OVERRIDE_APPROVE_STATUS: "7",
  OVERRIDE_REJECT_STATUS: "8",
  ORIGIN_CREATE: "CreateGrn",
  ORIGIN_APPROVER: "ViewApprover",
  ORIGIN_EDIT: "EditGrn",
  ORIGIN_INITIATOR: "ViewInitiator",
  ORIGIN_RETURN: "ReturnGrn",
  ORIGIN_GOODS_RETURN_HISTORY: "GoodsReturnHistory",
    ORIGIN_VIEW_HISTORY: "Viewhistory",
} as const;

// ============================================
// VIEW DETAIL REASON CATEGORY CODES
// ============================================
export const GRN_VIEW_DETAIL_REASON_CATEGORY = {
  APPROVE: "1",
  REJECT: "2",
  RETURN: "3",
  GOODS_RETURN_RETURN: "6",
  GOODS_RETURN_APPROVE: "8",
  GOODS_RETURN_REJECT: "9",
} as const;

// ============================================
// GOODS RETURN STATUS CODES
// ============================================
export const GRN_GOODS_RETURN_STATUS = {
  APPROVE: "10",
  REJECT: "11",
  RETURN: "13",
} as const;

// ============================================
// ALERT TYPES
// ============================================
export const GRN_ALERT_TYPE = {
  SUCCESS: "success",
  ERROR: "error",
} as const;

// ============================================
// RETURN GRN DEFAULTS
// ============================================
export const GRN_RETURN_DEFAULTS = {
  DOC_TYPE_FALLBACK: "RETURN",
  ENUSER_FALLBACK: "SYSTEM",
} as const;

// ============================================
// REGEX PATTERNS
// ============================================
export const GRN_REGEX = {
  // Max 3 digits before decimal, 2 after (for percentages)
  PERCENTAGE: /^\d{0,3}(\.\d{0,2})?$/,

  // Max 9 digits before decimal, 2 after (for amounts/charges)
  AMOUNT: /^\d{0,9}(\.\d{0,2})?$/,

  // Positive numbers with decimals
  POSITIVE_NUMBER: /^\d+(\.\d{0,2})?$/,
} as const;

// ============================================
// DATE FORMATS
// ============================================
export const GRN_DATE_FORMAT = {
  STANDARD: "YYYY-MM-DD",
  SETUP: "DD/MM/YYYY",
  DEFAULT: "0001-01-01",
  DEFAULT_WITH_TIME: "0001-01-01T00:00:00",
} as const;

export type DateFormat = typeof GRN_DATE_FORMAT[keyof typeof GRN_DATE_FORMAT];

// ============================================
// CURRENCY SETTINGS
// ============================================
export const GRN_CURRENCY = {
  CODE: "LKR",
  SYMBOL: "LKR",
  DECIMAL_PLACES: 2,
  DEFAULT_VALUE: "0.00",
  ZERO: 0,
} as const;

// ============================================
// APPROVAL WORKFLOW STATUS MAPPING
// ============================================
export const APPROVAL_WORKFLOW = {
  // When approval is enabled (PARAID 24 = Y)
  ENABLED: {
    SAVE_STATUS: 3, // HOLD
    SUBMIT_STATUS: 1, // SUBMIT
  },
  // When approval is disabled (PARAID 24 = N)
  DISABLED: {
    SAVE_STATUS: 7, // Direct to some final state
    SUBMIT_STATUS: 4, // APPROVE
  },
} as const;

// ============================================
// DEFAULT VALUES
// ============================================
export const GRN_DEFAULTS = {
  STRING: "",
  NUMBER: 0,
  ARRAY: [] as const,
  CURRENCY: "0.00",
  BOOLEAN_YES: "Y",
  BOOLEAN_NO: "N",
} as const;

// ============================================
// ITEM MENU OPTIONS
// ============================================
export const ITEM_MENU = {
  NEW_ITEM: "New Item",
} as const;

// ============================================
// DOCUMENT CATEGORIES
// ============================================
export const DOC_CATEGORY = {
  ITEM_BASED: "Item-Based Document Types",
} as const;

// Numeric document category codes used by backend APIs
// These are derived from existing working API calls, e.g.
// Doctype?COMPCODE=...&LOCCODE=...&DOCCATCODE=1&STS=A
export const DOC_CATEGORY_CODE = {
  ITEM_BASED: 1,
} as const;
